import arrayIncludes from 'array-includes';

import EventClass from '../EventClass';
import BrowserVisibility from './BrowserVisibility';
import KeyboardActivity from './KeyboardActivity';
import MouseActivity from './MouseActivity';
import ScrollActivity from './ScrollActivity';

const BrowserActivityModules = {
  BrowserVisibility,
  KeyboardActivity,
  MouseActivity,
  ScrollActivity,
};

/**
 * A list of activity types that must be active for the user to be considered engaged
 * @type {Array}
 */
const requiredActivities = ['BrowserVisibility'];

/**
 * Abstracts the various activity activity modules into a single engagement
 * property and event
 */
class BrowserActivity extends EventClass {
  /**
   * @param  {Object} config - configuration object to set specific settings in the instance
   * @param {Object} ...params - object of the rest of the parameters to easily pass to
   * super consturctor
   */
  constructor(config = {}, ...params) {
    super(config, ...params);
    const global = config.global || {};

    const extendedConfig = {
      ...this._config,
      global: {
        tolerance: 20,
        frequency: 1000,
        ...global,
      },
    };

    this._engaged = null;
    this._activityInstances = {};

    this.handleStateChange = this.handleStateChange.bind(this);
    this.getEngagements = this.getEngagements.bind(this);

    Object.keys(BrowserActivityModules).forEach((activity) => {
      const activityConfig = { ...extendedConfig.global, ...extendedConfig[activity] };

      const activityInstance = new BrowserActivityModules[activity](activityConfig);
      this._activityInstances[activity] = activityInstance;
      activityInstance.addEventListener('statechange', this.handleStateChange.bind(this, activity));
    });

    this._engaged = this._activityInstances.BrowserVisibility.active;
  }

  updateEngagedState() {
    const active = Object.keys(this._activityInstances)
      .filter(activityName => !arrayIncludes(requiredActivities, activityName))
      .some(activityName => this._activityInstances[activityName].active);

    const required = (!requiredActivities.length) ||
      requiredActivities.every(activityName => this._activityInstances[activityName].active);

    const nextEngagedState = required && active;

    if (this._engaged !== nextEngagedState) {
      this._engaged = nextEngagedState;

      this.dispatchEvent('statechange', this._engaged);
    }

    return nextEngagedState;
  }

  /**
   * Callback for events that runs the event emitting method
   * @private
   */
  handleStateChange() {
    this.updateEngagedState();
  }

  /**
   * @type {boolean} - Returns whether the user is considered engaged or not
   */
  get isEngaged() {
    return this.updateEngagedState();
  }

  /**
   * Returns a list of activity types and their state
   * @return {Object}
   */
  getEngagements() {
    const obj = {};
    Object.keys(this._activityInstances)
      .forEach((activityType) => {
        obj[activityType] = this._activityInstances[activityType].active;
      });
    return obj;
  }
}

export default BrowserActivity;
